/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React, { useCallback, useRef, useState } from "react";
import { Button } from "../Button";
import { DropArea } from "../DropArea";
import { Layout } from "../Layouts";
import { SortArea } from "../SortArea";
import { Typography } from "../Typography";

export interface Image {
  data: string;
  id: string;
}

const isEqualAsArray = <T,>(a: T[], b: T[], equal: (a: T, b: T) => boolean) => {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (!equal(a[i], b[i])) return false;
  }

  return true;
};

export interface ImageUploadAreaImageProps {
  image: Image;
  onDelete: (id: string) => void;
}

export const ImageUploadAreaImage = ({
  image,
  onDelete,
}: ImageUploadAreaImageProps) => {
  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <img
        src={image.data}
        css={css`
          width: 100%;
          aspect-ratio: 4 / 3;
          cursor: pointer;
          object-fit: cover;
        `}
        loading="lazy"
        tabIndex={-1}
        alt=""
      />
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1;
          width: 100%;
          height: 100%;
          background-image: linear-gradient(
            rgba(0, 0, 0, 0.5),
            rgba(0, 0, 0, 0) 30%
          );
        `}
      />
      {image.id && (
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding: 4px;
          `}
        >
          <Button
            type="button"
            size="small"
            icon={<OpenInNewIcon />}
            css={css`
              z-index: 2;
              color: white;
            `}
            as={"a"}
            href={image.data}
            target="_blank"
            rel="noopener noreferrer"
          />
          <Button
            type="button"
            icon={<DeleteIcon />}
            size="small"
            onClick={() => onDelete(image.id)}
            css={css`
              z-index: 2;
              color: white;
            `}
          />
        </div>
      )}
    </div>
  );
};

export interface ImageUploadAreaProps {
  images?: Image[];
  readBlob: (blob: Blob) => Promise<string>; // 圧縮が必要ならここで
  onUpload: (data: string[]) => void;
  onDelete: (id: string) => void;
  onReorder?: (ids: string[]) => void;
  renderImage?: (props: ImageUploadAreaImageProps) => React.JSX.Element;
  /**
   * 複数選択モードを有効にします。
   * 有効にした状態で画像をクリックすると単独選択され、
   * Cmd/Ctrl + クリックで選択トグル、Shift + クリックで範囲選択ができます。
   * 選択中の画像をドラッグすると、選択された全画像がまとめて並び替えされます。
   */
  multiSelect?: boolean;
}

export const ImageUploadArea = ({
  images,
  readBlob,
  onUpload,
  onDelete,
  onReorder,
  renderImage: ImageComponent = ImageUploadAreaImage,
  multiSelect,
}: ImageUploadAreaProps) => {
  const [items, setItems] = useState<Image[]>([]);
  const pendingRef = useRef<string[]>([]);
  const compressingRef = useRef(0);

  const handleDrop = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      compressingRef.current += files.length;
      files.forEach((file) => {
        void (async () => {
          const data = await readBlob(file);
          setItems((prev) => [
            ...prev,
            {
              data,
              id: `${Date.now()}-${Math.random()}`,
            },
          ]);
          pendingRef.current.push(data);
          compressingRef.current -= 1;
          if (compressingRef.current === 0) {
            onUpload(pendingRef.current);
            pendingRef.current = [];
            setItems([]);
          }
        })();
      });
    },
    [readBlob, onUpload],
  );
  const handleUpload = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files!);
    handleDrop(files);
  };

  // PERF: 初回のレンダリング時に今と同じ並びのままreorderが呼ばれてしまって無意味なAPIコールが入るので、配列が変わってる時だけonReorderを呼ぶようにした方がいいかもしれない
  const handleReorder = (imagesNew: Image[]) => {
    // 初回の自明な変更でonReorderが意図せずよばれないように
    if (
      images &&
      images.length > 0 &&
      imagesNew.length > 0 &&
      !isEqualAsArray(images, imagesNew, (a, b) => a.id === b.id)
    ) {
      onReorder?.(imagesNew.map((i) => i.id));
    }
  };

  return (
    <>
      <div
        css={css`
          display: grid;
          gap: 16px;
          max-width: 1024px;
        `}
      >
        <DropArea
          onDrop={handleDrop}
          css={css`
            width: 100%;
            height: 200px;
          `}
          accept={/image\/*/}
        >
          <Layout.Column gap={2} alignItems="center">
            <Button
              as="label"
              htmlFor="upload"
              color="primary"
              variant="filled"
            >
              写真をアップロード
            </Button>
            <input
              type="file"
              accept="image/*"
              name="upload"
              id="upload"
              multiple
              onChange={handleUpload}
              css={css`
                display: none;
              `}
            />
            <Typography variant="caption">
              あるいは画像をここにドラッグ
            </Typography>
          </Layout.Column>
        </DropArea>
        <SortArea
          gap={2}
          disabled={!onReorder}
          onChange={handleReorder}
          list={images}
          multiSelect={multiSelect}
          css={css`
            display: grid;
            grid-template-columns: repeat(4, 1fr);

            @media screen and (max-width: 768px) {
              grid-template-columns: repeat(2, 1fr);
            }
          `}
        >
          {[...(images ?? []), ...items].map((image) => {
            return (
              // ここでImageComponentを直書きするとなぜかSortAreaから渡ってきたprops(onDragStartなど)がImageComponentのrootのDOMに渡らなくなってしまう
              // 解決策としてImageComponentsで任意のpropsを受け取れるようにしrootのdivに渡すというのでも良いが、ユーザーランドでこの処理を挟むのを忘れる恐れがあるので代わりにdivで囲うことで対応した
              <div key={image.id}>
                <ImageComponent image={image} onDelete={onDelete} />
              </div>
            );
          })}
        </SortArea>
      </div>
    </>
  );
};
